import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import FormInput from '../../components/forms/FormInput';
import PasswordInput from '../../components/forms/PasswordInput';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useToast } from '../../components/common/Toast';
import { authService } from '../../services/auth.service';
import { setCredentials } from '../../store/slices/authSlice';
import { useForm } from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { theme } from '../../config/theme';
import { USER_ROLES } from '../../config/constants';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState('');

  const { values, errors, touched, handleChange, handleBlur, validate } = useForm(
    { email: '', password: '' },
    {
      email: (value) => validators.email(value),
      password: (value) => validators.required(value, 'Password'),
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setLoginError('');
    try {
      const response = await authService.login(values);
      
      // Unwrap the ApiResponse data safely
      const respData = response?.data?.data || response?.data || response || {};
      const role = respData?.user?.role || response?.data?.user?.role;
      
      // Seller registered but OTP not verified yet — redirect to phone verification without logging in
      if (role === USER_ROLES.SELLER && respData.phoneNotVerified) {
        sessionStorage.setItem('seller_temp_data', JSON.stringify({ phone: respData.phone }));
        toast.success('Login successful! Please verify your phone to continue.');
        navigate('/seller/verify-otp');
        return;
      }
      
      // For all other cases, log the user into Redux
      dispatch(setCredentials(response));

      if (role === USER_ROLES.SELLER && respData.onboardingIncomplete) {
        navigate('/seller/onboarding/status');
      } else if (role === USER_ROLES.CUSTOMER) {
        navigate('/');
      } else if (role === USER_ROLES.SELLER) {
        navigate('/seller/dashboard');
      } else if (role === USER_ROLES.ADMIN) {
        navigate('/admin/dashboard');
      }
      toast.success('Login successful!');
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.message || 'Login failed. Please check your credentials.';
      setLoginError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Styles for the side-by-side logo and text
  const logoContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    textDecoration: 'none',
  };

  const logoTextStyles = {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  };

  return (
    <div style={{ width: '100%', maxWidth: '450px' }}>
      <div style={{ textAlign: 'center', marginBottom: theme.spacing['2xl'] }}>
        
        {/* Updated Logo Section */}
        <Link to="/" style={logoContainerStyles}>
          <img 
            src="/images/logo.png" 
            alt="AlarcTrade Logo" 
            style={{ 
              height: '48px', // Slightly larger for the login header
              width: 'auto', 
              objectFit: 'contain' 
            }} 
          />
          <span style={logoTextStyles}>
            AlarcTrade
          </span>
        </Link>
        
        <p style={{ fontSize: theme.typography.fontSize.base, color: theme.colors.text.secondary }}>
          Sign in to your account
        </p>
      </div>

      <Card padding="xl">
        <h2 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, textAlign: 'center', marginBottom: theme.spacing.xl }}>
          Login
        </h2>

        {loginError && (
          <div style={{
            backgroundColor: `${theme.colors.error}15`,
            color: theme.colors.error,
            padding: theme.spacing.md,
            borderRadius: theme.borderRadius.md,
            marginBottom: theme.spacing.lg,
            fontSize: theme.typography.fontSize.sm,
            textAlign: 'center',
            border: `1px solid ${theme.colors.error}30`
          }}>
            {loginError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <FormInput
            label="Email Address"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="your@email.com"
            error={errors.email}
            touched={touched.email}
            required
          />

          <PasswordInput
            label="Password"
            name="password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your password"
            error={errors.password}
            touched={touched.password}
            required
          />

          <div style={{ textAlign: 'right', marginBottom: theme.spacing.md }}>
            <Link 
              to="/forgot-password" 
              style={{ 
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.primary.main, 
                textDecoration: 'none',
                fontWeight: theme.typography.fontWeight.medium 
              }}
            >
              Forgot Password?
            </Link>
          </div>

          <Button type="submit" fullWidth loading={loading} disabled={loading}>
            Sign In
          </Button>
        </form>

        <div style={{ marginTop: theme.spacing.xl, textAlign: 'center', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: theme.colors.primary.main, textDecoration: 'none', fontWeight: theme.typography.fontWeight.medium }}>
            Sign Up
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default Login;